import {css, StyleSheet} from 'aphrodite';
import React, {ChangeEvent} from 'react';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import requireEsignature, {EsignFailureError} from 'notebook/reviews/requireEsignature';
import {Review, ReviewAction} from 'notebook/reviews/ReviewConstants';
import {ReviewCriteriaGroup, ReviewCriterionStatus} from 'notebook/reviews/ReviewCriteriaConstants';
import * as ReviewDAO from 'notebook/reviews/ReviewDAO';
import notifications, {GenericResponseJSON} from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  getReviewStatusActionMetadata,
  REVIEW_STATUS_ACTION_ENDED_EVENT,
  REVIEW_STATUS_ACTION_STARTED_EVENT,
} from 'performance/processReviewsEvents';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import * as Colors from 'ui-library/Colors';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import Modal from 'ui-library/modals/Modal';
import {OngoingTaskResponse} from 'util/TaskRunner';
import {MakeFieldsOptional, TextAreaChangeEvent} from 'util/Types';

import {EsignatureStatement} from './EsignatureStatement';
import {ReviewComment} from './ReviewComment';
import * as ReviewUtil from './ReviewUtil';

export interface ReviewStageInfo {
  id: string;
  name?: string;
  description: string;
}

export interface ReviewStatusModalProps {
  action: ReviewAction;
  fileId: string;
  reviewId: string;
  reviewCriteriaGroups: Array<ReviewCriteriaGroup>;
  onRequestClose: () => void;
  onUpdate?: (
    fileId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
  title?: string;
  stageInfo?: ReviewStageInfo;
  isForReviewLifecycle?: boolean;
  message: string;
  /** Custom rendering of additional info related to the review stage */
  renderAdditionalReviewInfo?: (fileId: string, reviewAction: ReviewAction) => JSX.Element | null;
  /** Custom error rendering, configured from ReviewBadge. */
  renderReviewError?: (error: {responseJSON?: GenericResponseJSON}) => JSX.Element | null;
  performanceMetadata?: {[key: string]: string}; // takes in performance metric
}

interface State {
  message: string;
  error: JSX.Element | null;
  submitting: boolean;
}

function generateEvaluationSnapshotForAction(action: ReviewAction, fileId: string): Promise<void> {
  if (action !== ReviewAction.SEND_FOR_REVIEW) {
    return Promise.resolve();
  }

  return ReviewUtil.ensureCurrentRuntimeSnapshotPersisted(fileId);
}

/*
This modal is used to accept, reject, retract, comment on, and re-send reviews that already exist.
It is not used to create (request) reviews -- that is CreateUpdateReviewModal.
*/
export default class ReviewStatusModal extends React.Component<ReviewStatusModalProps, State> {
  textareaRef = React.createRef<HTMLTextAreaElement>();

  static open(props: MakeFieldsOptional<ReviewStatusModalProps, 'onRequestClose'>): void {
    LegacyModalContainer.open(
      ReviewStatusModal,
      <ReviewStatusModal
        onRequestClose={() => {
          LegacyModalContainer.close(ReviewStatusModal);
        }}
        {...props}
      />
    );
  }

  private _isMounted = false;

  constructor(props: ReviewStatusModalProps) {
    super(props);
    this.state = {
      message: this.props.message,
      error: null,
      submitting: false,
    };
  }

  override componentDidMount(): void {
    this._isMounted = true;
    const textarea = this.textareaRef.current;
    if (textarea != null) {
      selutil.restoreSelection(textarea, {start: textarea.value.length, end: textarea.value.length});
    }
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  private getTitle(): string {
    if (this.props.title) {
      return this.props.title;
    }
    return (() => {
      switch (this.props.action) {
        case ReviewAction.ACCEPT:
          return 'Accepting';
        case ReviewAction.REJECT:
          return 'Requesting changes';
        case ReviewAction.RETRACT:
          return 'Retracting';
        case ReviewAction.SEND_FOR_REVIEW:
          return 'Resending for review';
        case ReviewAction.COMMENT:
          return 'Leaving a comment';
        default:
          throw new Error(`Unexpected action ${this.props.action}`);
      }
    })();
  }

  private _reviewCriterionStatuses(): {[criterionId: string]: ReviewCriterionStatus} {
    const statuses: {[criterionId: string]: ReviewCriterionStatus} = {};
    for (const criteriaGroup of this.props.reviewCriteriaGroups) {
      for (const criterion of criteriaGroup.reviewCriteria) {
        statuses[criterion.id!] = criterion.status;
      }
    }
    return statuses;
  }

  private getErrorBanner = (error: string): JSX.Element => (
    <InlineBanner bannerType={InlineBannerType.ERROR} text={error} />
  );

  submit = async (): Promise<void> => {
    const metadata = getReviewStatusActionMetadata(
      this.props.fileId,
      this.props.action,
      this.props.performanceMetadata
    );

    // Track specific event with id since it is possible that multiple accept or rejects can be done by the
    // user for a single review.
    const eventId = uuidv4();
    logProfilerTimingEvent({...REVIEW_STATUS_ACTION_STARTED_EVENT, id: eventId}, metadata);
    this.setState({submitting: true, error: null});

    const data = {
      action: this.props.action,
      message: this.state.message,
      reviewCriterionStatuses: this._reviewCriterionStatuses(),
      stageId: this.props.stageInfo?.id,
    };

    try {
      const signatureInfo = await requireEsignature();
      await generateEvaluationSnapshotForAction(this.props.action, this.props.fileId);
      const {review, filePermissions, snapshotTask} = await ReviewDAO.createReviewChange(
        this.props.fileId,
        this.props.reviewId,
        signatureInfo,
        data
      );
      notifications.success('Successfully updated review', {duration: 5});

      if (this.props.onUpdate) {
        this.props.onUpdate(this.props.fileId, review, filePermissions, snapshotTask);
      }

      LegacyModalContainer.close(ReviewStatusModal);
      logProfilerTimingEvent({...REVIEW_STATUS_ACTION_ENDED_EVENT, id: eventId}, metadata);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      assert(error != null, 'Found null error during submit for review.');
      const reviewErrorElem =
        this.props.renderReviewError != null ? this.props.renderReviewError(error) : null;
      if (reviewErrorElem != null) {
        this.setState({error: reviewErrorElem});
      } else if (error instanceof EsignFailureError) {
        this.setState({error: this.getErrorBanner(error.message)});
      } else {
        const friendlyError = notifications.getFriendlyError(error);
        if (friendlyError && this._isMounted) {
          this.setState({error: this.getErrorBanner(friendlyError)});
        } else {
          notifications.notifyError(error);
        }
      }
    }
    if (this._isMounted) {
      this.setState({submitting: false});
    }
  };

  updateMessage = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    this.setState({message: e.target.value});
  };

  private _isMessageRequired(): boolean {
    const {isForReviewLifecycle, action} = this.props;
    // We never *require* comments for review lifecycle based review action, since the review stage info
    // should be enough. Unless it's a comment action.
    if (isForReviewLifecycle) {
      return action === ReviewAction.COMMENT;
    } else if (!BenchlingConfig.ENABLE_ELN_REVIEW_COMMENTS) {
      return false;
    } else if (action === ReviewAction.REJECT) {
      return BenchlingConfig.ELN_REVIEW_REJECTION_REQUIRES_COMMENT;
    }
    return [ReviewAction.COMMENT, ReviewAction.RETRACT].includes(action);
  }

  private shouldDisableCommentBox(): boolean {
    if (this.state.submitting) {
      return true;
    }
    return !(this.props.isForReviewLifecycle || BenchlingConfig.ENABLE_ELN_REVIEW_COMMENTS);
  }

  override render(): JSX.Element {
    const {stageInfo, action} = this.props;
    const esigInfo = <EsignatureStatement action={action} buttonText="Submit" />;
    return (
      <Modal
        closeable={!this.state.submitting}
        title={this.getTitle()}
        showFooterBorder={false}
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this.submit}
        buttons={[
          {
            text: !this.state.submitting ? 'Submit' : 'Submitting...',
            submit: true,
            disabled: (this.state.message.length === 0 && this._isMessageRequired()) || this.state.submitting,
          },
        ]}
        enableRedesign={true}
      >
        {this.state.error && <div className="u-spacing-bottom">{this.state.error}</div>}
        {esigInfo}
        {stageInfo != null ? (
          <FormGroup className={css(styles.formGroup)}>
            <Label className={css(styles.sectionTitle, styles.label)}>{stageInfo.name}</Label>
            <p>{stageInfo.description}</p>
          </FormGroup>
        ) : null}
        {this.props.renderAdditionalReviewInfo != null
          ? this.props.renderAdditionalReviewInfo(this.props.fileId, action)
          : null}
        <FormGroup>
          <ReviewComment
            comment={this.state.message}
            onChange={(e: TextAreaChangeEvent) => {
              this.setState({message: e.target.value});
            }}
            rowCount={20}
            disabled={this.shouldDisableCommentBox()}
            showRedAsterisk={this._isMessageRequired()}
          />
        </FormGroup>
        {window.impersonating ? (
          <strong style={{color: 'red'}}>
            DO NOT CLICK THE SUBMIT BUTTON WHILE IMPERSONATING A REAL USER
          </strong>
        ) : null}
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  sectionTitle: {
    color: Colors.NEAR_BLACK,
    fontSize: 15,
  },
  label: {
    marginBottom: 15,
  },
  formGroup: {
    paddingBottom: '8px',
  },
});
